import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { RiPaintBrushLine, RiArrowLeftLine } from '@remixicon/react';
import { ReportNavContext } from '../../components/reportNavContext';
import { useTranslation } from 'react-i18next';
import PageShell from '../../components/PageShell';
import CardShell from '../../components/CardShell';
import { Cell, Choix } from '../../components/InlineCells';
import { useP } from '../../i18n/useP';
import { getSourceMaps, putSourceMap, deleteSourceMap } from '../../api/salesSourceMaps';
import { listOffers } from '../../api/salesOffers';
import { getImportSettings, putImportSetting } from '../../api/salesBillingImport';
import './Sales.css';

/* CORRESPONDANCES (décision d'Olivier, 2026-10-22) : à quelle offre appartient chaque libellé du réalisé collé
   depuis l'outil du client (catégories de produits d'Odoo, familles, lignes d'un Excel), comme un compte comptable
   se rattache à sa ligne de reporting. D'abord les libellés À RATTACHER, des lignes de réalisé sans offre (le champ
   attendu se lit en ambre), puis les correspondances posées. Choisir une offre rattache aussi les lignes déjà
   collées, de tous les exercices ; « ignorer » les supprime et écarte les collages suivants.

   LE PINCEAU (demande d'Olivier, 2026-10-22, comme le mapping des comptes de Finance) : on arme une offre (ou
   « ignorer », ou « à rattacher » pour retirer), puis on clique ou on glisse sur les libellés. Chaque libellé se pose
   aussitôt, sur place : la liste ne se réordonne pas tant que le pinceau est en main, sans quoi un clic tomberait
   sur le libellé qui vient de prendre la place du précédent. */
const IGNORER = '__ignore';
const RETIRER = '__unmap';

export default function SourceMapPage({ activeTenant, addToast, canWrite = false }) {
  const { t } = useTranslation();
  const p = useP();
  const nav = useContext(ReportNavContext);
  const tid = activeTenant?.id;
  const [entries, setEntries] = useState([]);
  const [offers, setOffers] = useState([]);
  const [signs, setSigns] = useState(null);       // le sens des montants de chaque nature : { billing, ca }
  const [drafts, setDrafts] = useState([]);       // des libellés qu'on pose avant tout collage : { k, label }
  const [loading, setLoading] = useState(false);
  const [pinceau, setPinceau] = useState(false);     // le mode pinceau
  const [arme, setArme] = useState(null);            // ce que le pinceau pose : une offre, IGNORER ou RETIRER
  const peint = useRef(false);                       // le bouton est enfoncé : on peint en glissant
  const toast = useCallback((m, type) => { if (addToast) addToast(m, type); }, [addToast]);

  const reload = useCallback(async () => {
    if (!tid) return;
    const [m, o, g] = await Promise.all([getSourceMaps(tid), listOffers(tid), getImportSettings(tid)]);
    if (m.error) { toast(m.error, 'error'); return; }
    setEntries((m.data && m.data.entries) || []);
    setOffers(o.error ? [] : (o.data || []));
    setSigns(g && g.data ? g.data : null);
  }, [tid, toast]);
  useEffect(() => {
    if (!tid) return;
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [tid, reload]);

  const options = [{ value: '', label: p('à rattacher') },
                   ...offers.map((o) => ({ value: o.id, label: o.label || o.name })),
                   { value: IGNORER, label: p('ignorer') }];

  const appliquer = async (label, choix, e = null) => {
    if (choix === IGNORER && e && e.free > 0
        && !window.confirm(`${p('Les lignes de réalisé « %s » sans offre seront supprimées').replace('%s', label)} (${e.free}). ${p('Continuer ?')}`)) return false;
    const r = await putSourceMap(tid, choix === IGNORER ? { label, ignored: true } : { label, offer_id: choix });
    if (r.error) { toast(r.error, 'error'); return false; }
    const d = r.data || {};
    if (d.attached) toast(`${d.attached} ${p('lignes de réalisé rattachées')}`);
    if (d.deleted) toast(`${d.deleted} ${p('lignes de réalisé supprimées')}`);
    await reload();
    return true;
  };
  const choisir = async (e, choix) => {
    if (!choix) {                                    // « à rattacher » : la correspondance posée se retire
      if (e.id) {
        const r = await deleteSourceMap(tid, e.id);
        if (r.error) toast(r.error, 'error');
        await reload();
      }
      return;
    }
    await appliquer(e.label, choix, e);
  };
  const choisirBrouillon = async (d, choix) => {
    if (!choix) return;
    if (!d.label.trim()) { toast(p("Saisir d'abord le libellé."), 'error'); return; }
    if (await appliquer(d.label, choix)) setDrafts((x) => x.filter((y) => y.k !== d.k));
  };
  const supprimer = async (e) => {
    const r = await deleteSourceMap(tid, e.id);
    if (r.error) toast(r.error, 'error');
    await reload();
  };
  const regler = async (kind, sign) => {
    const r = await putImportSetting(tid, kind, sign);
    if (r.error) { toast(r.error, 'error'); return; }
    setSigns(r.data);
  };
  const finPinceau = useCallback(() => { setPinceau(false); setArme(null); reload(); }, [reload]);
  useEffect(() => {
    if (!pinceau) return undefined;
    const lacher = () => { peint.current = false; };
    const touche = (ev) => { if (ev.key === 'Escape') finPinceau(); };
    window.addEventListener('mouseup', lacher);
    window.addEventListener('keydown', touche);
    return () => { window.removeEventListener('mouseup', lacher); window.removeEventListener('keydown', touche); };
  }, [pinceau, finPinceau]);
  const armer = (choix) => {
    if (choix === IGNORER && !window.confirm(p('Les lignes de réalisé sans offre des libellés que tu vas peindre seront supprimées. Armer « ignorer » ?'))) return;
    setArme(choix);
  };
  /* Pose le pinceau sur un libellé, sur place (aucun rechargement : l'ordre ne bouge pas). */
  const peindre = async (e) => {
    if (!arme) return;
    const etat = arme === IGNORER ? { ignored: true, offer_id: null } : { ignored: false, offer_id: arme === RETIRER ? null : arme };
    if (Boolean(e.ignored) === etat.ignored && (e.offer_id || null) === etat.offer_id) return;     // déjà ainsi
    if (arme === RETIRER && !e.id) return;
    const r = arme === RETIRER ? await deleteSourceMap(tid, e.id)
      : await putSourceMap(tid, etat.ignored ? { label: e.label, ignored: true } : { label: e.label, offer_id: arme });
    if (r.error) { toast(r.error, 'error'); return; }
    const d = r.data || {};
    setEntries((x) => x.map((y) => (y.label === e.label
      ? { ...y, ...etat, id: arme === RETIRER ? null : d.id, free: arme === RETIRER ? y.free : 0,
          rows: etat.ignored ? Math.max(0, y.rows - (d.deleted || 0)) : y.rows }
      : y)));
  };
  const ajouter = () => setDrafts((x) => [...x, { k: `d${Date.now()}${x.length}`, label: '' }]);

  if (!tid) return <PageShell framed title={t('nav:items.sales-source-maps')}><p className="om-empty">{p('Choisir un client.')}</p></PageShell>;
  const restants = entries.filter((e) => !e.ignored && !e.offer_id).length;
  const nom = (id) => (offers.find((o) => o.id === id) || {}).label || (offers.find((o) => o.id === id) || {}).name;
  return (
    <PageShell framed title={t('nav:items.sales-source-maps')}>
      <div className="sm-pile">
      {/* LE RETOUR (2026-10-22) : cette page n'a plus d'entrée au menu, on y vient des Revenus réalisés et on y retourne d'ici. */}
      {nav && nav.setPage && (
        <button type="button" className="lk-btn lk-btn--quiet sm-retour" onClick={() => nav.setPage('sales-billing')}>
          <RiArrowLeftLine size={14} aria-hidden="true" />{p('Retour aux revenus réalisés')}
        </button>
      )}
      {/* LE SENS DES MONTANTS d'un fichier importé (2026-10-22), par nature : les produits y sont en positif (la
          facturation d'Odoo) ou en négatif (le solde d'écritures comptables). La fenêtre d'importation le propose. */}
      {signs && (
        <CardShell title={p('Sens des montants importés')} flush className="sm-card">
          <div className="lk-hscroll">
            <table className="lk-entry-table sm-table">
              <colgroup><col style={{ width: 380 }} /><col style={{ width: 280 }} /><col style={{ width: 110 }} /></colgroup>
              <thead>
                <tr><th className="sm-lbl">{p('Donnée')}</th><th className="sm-lbl">{p('Les produits sont en')}</th><th /></tr>
              </thead>
              <tbody>
                {[['ca', p("Chiffre d'affaires")], ['billing', p('Facturation')]].map(([kind, libelle]) => (
                  <tr key={kind} className="sm-row">
                    <td className="sm-lbl">{libelle}</td>
                    <td className="sm-lbl">
                      <Choix value={signs[kind]} canWrite={canWrite} onSave={(v) => v && regler(kind, v)}
                             options={[{ value: 'positive', label: p('positif') }, { value: 'negative', label: p('négatif') }]} />
                    </td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardShell>
      )}
      <CardShell title={p('Libellés du réalisé')} flush className="sm-card"
                 primaryAction={canWrite ? (
                   <>
                     {/* Ce qui reste à rattacher se compte, comme dans le mapping des comptes. */}
                     {restants > 0 && <span className="sm-reste">{restants} {p('à rattacher')}</span>}
                     <button type="button" className={`lk-btn lk-btn--toggle${pinceau ? ' is-on' : ''}`} aria-pressed={pinceau}
                             onClick={() => (pinceau ? finPinceau() : setPinceau(true))}>
                       <RiPaintBrushLine aria-hidden="true" />{p('Pinceau')}
                     </button>
                     {!pinceau && <button type="button" className="lk-btn" onClick={ajouter}>＋ {p('Correspondance')}</button>}
                   </>
                 ) : null}>
        {pinceau && (
          <div className="sm-brush">
            <div className="sm-pal">
              {offers.map((o) => (
                <button key={o.id} type="button" className={`sm-chip${arme === o.id ? ' is-on' : ''}`} aria-pressed={arme === o.id}
                        onClick={() => armer(o.id)}>{o.label || o.name}</button>
              ))}
              <span className="sm-pal__sep" aria-hidden="true" />
              <button type="button" className={`sm-chip${arme === IGNORER ? ' is-on' : ''}`} aria-pressed={arme === IGNORER}
                      onClick={() => armer(IGNORER)}>{p('ignorer')}</button>
              <button type="button" className={`sm-chip${arme === RETIRER ? ' is-on' : ''}`} aria-pressed={arme === RETIRER}
                      onClick={() => armer(RETIRER)}>{p('à rattacher')}</button>
            </div>
            <p className="sm-brush__hint">{arme ? p('Cliquer ou glisser sur les libellés.') : p('Choisir une offre.')}</p>
          </div>
        )}
        {loading ? <p className="sales-loading">{p('Chargement…')}</p> : (
          <div className="lk-hscroll">
            <table className={`lk-entry-table sm-table${pinceau ? ' is-pinceau' : ''}${pinceau && arme ? ' is-arme' : ''}`}>
              <colgroup><col style={{ width: 380 }} /><col style={{ width: 280 }} /><col style={{ width: 110 }} /></colgroup>
              <thead>
                <tr>
                  <th className="sm-lbl">{p('Libellé du réalisé')}</th>
                  <th className="sm-lbl">{p('Offre')}</th>
                  <th className="sm-num">{p('Lignes')}</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((d) => (
                  <tr key={d.k} className="sm-row">
                    <td className="sm-lbl">
                      <button type="button" className="lk-x lk-del sm-del" aria-label={p('Supprimer')}
                              onClick={() => setDrafts((x) => x.filter((y) => y.k !== d.k))}>✕</button>
                      <Cell value={d.label} canWrite placeholder={p('Libellé du réalisé')}
                            onSave={(v) => setDrafts((x) => x.map((y) => (y.k === d.k ? { ...y, label: v || '' } : y)))} />
                    </td>
                    <td className="sm-lbl"><Choix value="" canWrite options={options} tone="lk-todo" onSave={(v) => choisirBrouillon(d, v)} /></td>
                    <td className="sm-num">–</td>
                  </tr>
                ))}
                {entries.map((e) => {
                  const pose = Boolean(e.ignored || e.offer_id);
                  return (
                    <tr key={e.id || e.label} className="sm-row"
                        onMouseDown={pinceau && arme ? (ev) => { ev.preventDefault(); peint.current = true; peindre(e); } : undefined}
                        onMouseEnter={pinceau && arme ? () => { if (peint.current) peindre(e); } : undefined}>
                      <td className="sm-lbl">
                        {canWrite && e.id && !pinceau && (
                          <button type="button" className="lk-x lk-del sm-del" aria-label={p('Supprimer')} onClick={() => supprimer(e)}>✕</button>
                        )}
                        {e.label}
                      </td>
                      <td className="sm-lbl">
                        <Choix value={e.ignored ? IGNORER : (e.offer_id || '')} canWrite={canWrite} options={options}
                               tone={pose ? '' : 'lk-todo'} onSave={(v) => choisir(e, v)} />
                        {!canWrite && e.offer_id && <span className="sr-only">{nom(e.offer_id)}</span>}
                      </td>
                      <td className="sm-num">{e.rows || '–'}</td>
                    </tr>
                  );
                })}
                {!drafts.length && !entries.length && (
                  <tr><td colSpan={3} className="om-empty">{p('Aucun libellé.')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardShell>
      </div>
    </PageShell>
  );
}
